import './App.css';
import 'primereact/resources/themes/bootstrap4-light-blue/theme.css';
import 'primeicons/primeicons.css';
import { BrowserRouter, Routes, Route, useLocation, useParams } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from 'react';
import { getPermissionsSet } from './utils/apiService';
import { PermissionPages } from './consts/PermissionConstants';
import { ProgressSpinner } from 'primereact/progressspinner';
import 'primeflex/primeflex.css';

// Lazy load all route components
const Master = lazy(() => import('./layouts/master'));
const Login = lazy(() => import('./layouts/login'));
const Profile = lazy(() => import('./layouts/profile'));
const Notifications = lazy(() => import('./layouts/notifications'));
const PlayerSearch = lazy(() => import('./layouts/player-search'));
const PlayerAdd = lazy(() => import('./layouts/player-add'));
const PlayerView = lazy(() => import('./layouts/player-view'));
const PlayerContactsEdit = lazy(() => import('./layouts/player-contacts-edit'));
const PlayerDetailsEdit = lazy(() => import('./layouts/player-details-edit'));
const ClubSearch = lazy(() => import('./layouts/club-search'));
const ClubRequirements = lazy(() => import('./layouts/club-requirements').then(module => ({ default: module.ClubRequirements })));
const ClubRequirementsAdd = lazy(() => import('./layouts/club-requirements-add'));
const ClubView = lazy(() => import('./layouts/club-view'));
const ClubStaffEdit = lazy(() => import('./layouts/club-staff-edit'));
const ClubDetailsEdit = lazy(() => import('./layouts/club-details-edit'));
const ClubAdd = lazy(() => import('./layouts/club-add'));
const LeagueManager = lazy(() => import('./layouts/league-manager'));
const Analysis = lazy(() => import('./layouts/analysis'));
const MatchReport = lazy(() => import('./layouts/match-reports').then(module => ({ default: module.MatchReport })));
const MatchReportsAdd = lazy(() => import('./layouts/match-reports-add'));
const MatchReportsView = lazy(() => import('./layouts/match-reports-view'));
const CandidateAdd = lazy(() => import('./layouts/candidate-add'));
const TransferSheets = lazy(() => import('./layouts/transfer-sheets'));
const ResetClub = lazy(() => import('./layouts/reset-club'));
const ExternalLogin = lazy(() => import('./components/ExternalLogin'));
const MatchReportPublic = lazy(() => import('./layouts/match-report-public'));
// Map route paths to arrays of page IDs
const routePageIds = {
  "Profile": [],
  "Notifications": [],

  "Player-search": [PermissionPages.Player.PlayerSearch],
  "Player-add": [PermissionPages.Player.PlayerAdd],
  "Club-search": [PermissionPages.Club.ClubSearch],
  "Club-requirements": [PermissionPages.Club.Requirements],
  "Club-requirements-add": [PermissionPages.Club.Requirements, PermissionPages.Club.RequirementsChangeCreatedBy],
  "Club-requirement-edit/:id": [PermissionPages.Club.Requirements, PermissionPages.Club.RequirementsChangeCreatedBy],
  "Club-add": [PermissionPages.Club.ClubAdd],
  "League-manager": [PermissionPages.Club.LeagueManager],
  "Match-reports": [PermissionPages.Club.MatchReports],
  "Match-reports-add": [PermissionPages.Club.MatchReports],
  "Match-reports-edit/:id": [PermissionPages.Club.MatchReports],
  "Match-reports-view/:id": [PermissionPages.Club.MatchReports],
  "Candidate-add/:id": [PermissionPages.Club.Requirements],
  "Transfer-sheets": [PermissionPages.Club.TransferSheets],
  "Reset-club": [PermissionPages.Admin.ResetClubRequirements],

  "Club-staff-edit/:id": [PermissionPages.Club.ClubStaff, PermissionPages.Club.ClubDetails, PermissionPages.Club.StellarContacts],
  "Club-details-edit/:id": [PermissionPages.Club.ClubDetails, PermissionPages.Club.StellarContacts],
  "Club-view/:id": [PermissionPages.Club.ClubDetails, 
    PermissionPages.Club.StellarContacts, 
    PermissionPages.Club.ClubStaff, 
    PermissionPages.Club.Requirements, 
    PermissionPages.Club.ClubFiles],

  "Player-view/:id": [PermissionPages.Player.StellarContacts, 
    PermissionPages.Player.TopSection, 
    PermissionPages.Player.PlayerAdmin, 
    PermissionPages.Player.PlayerDetailsKeyContacts,
    PermissionPages.Player.RepresentationContract,
    PermissionPages.Player.ClubContract,
    PermissionPages.Player.PlayerInfo,
    PermissionPages.Player.Comments,
    PermissionPages.Player.ClubDetails,
    PermissionPages.Player.PlayerFiles,
    PermissionPages.Club.MatchReports,
    PermissionPages.Club.Requirements],
  "Player-contacts-edit/:id": [PermissionPages.Player.StellarContacts, PermissionPages.Player.TopSection, PermissionPages.Player.PlayerDetailsKeyContacts, PermissionPages.Player.ClubDetails],
  "Player-details-edit/:id": [PermissionPages.Player.PlayerAdd, PermissionPages.Player.StellarContacts, PermissionPages.Player.TopSection, PermissionPages.Player.PlayerDetailsKeyContacts, PermissionPages.Player.ClubDetails],

  "Analysis": [PermissionPages.Admin.Analysis],
};

// Wrapper to fetch permissions and inject as prop
function PermissionRoute({ element: Element, pageIds, getIdFromParams, ...rest }) {
  const params = useParams();
  const [permissions, setPermissions] = useState(null);
  useEffect(() => {
    let isMounted = true;
    async function fetchPermissions() {
      let perms = {};
      let idArg = getIdFromParams ? getIdFromParams(params) : undefined;
      let promises = [];
      for (const pageId of pageIds) {
        promises.push(getPermissionsSet(pageId, idArg || 0).then(x => perms[pageId] = x));
      }
      await Promise.all(promises);
      if (isMounted) setPermissions(perms);
    }
    fetchPermissions();
    return () => { isMounted = false; };
  }, [pageIds, params, getIdFromParams]);
  // Pass permissions as prop
  if(!permissions) {
    return <div style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(255,255,255,0.7)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <ProgressSpinner style={{width: '50px', height: '50px'}} />
            </div>;
  }
  return <Element {...rest} permissions={permissions} />;
}

function AppRoutes() {
  const location = useLocation();
  
  return (
    <Routes location={location} key={location.pathname}>
      <Route path="Profile" element={
        <PermissionRoute element={Profile} pageIds={routePageIds["Profile"]} />
      } />
      <Route path="Notifications" element={
        <PermissionRoute element={Notifications} pageIds={routePageIds["Notifications"]} />
      } />
      <Route path="Player-search" element={
        <PermissionRoute element={PlayerSearch} pageIds={routePageIds["Player-search"]} />
      } />
      <Route path="Player-add" element={
        <PermissionRoute element={PlayerAdd} pageIds={routePageIds["Player-add"]} />
      } />
      <Route path="Player-view/:id" element={
        <PermissionRoute
          element={PlayerView}
          pageIds={routePageIds["Player-view/:id"]}
          getIdFromParams={params => params.id}
        />
      } />
      <Route path="Player-contacts-edit/:id" element={
        <PermissionRoute
          element={PlayerContactsEdit}
          pageIds={routePageIds["Player-contacts-edit/:id"]}
          getIdFromParams={params => params.id}
        />
      } />
      <Route path="Player-details-edit/:id" element={
        <PermissionRoute
          element={PlayerDetailsEdit}
          pageIds={routePageIds["Player-details-edit/:id"]}
          getIdFromParams={params => params.id}
        />
      } />
      <Route path="Club-search" element={
        <PermissionRoute element={ClubSearch} pageIds={routePageIds["Club-search"]} />
      } />
      <Route path="Club-requirements" element={
        <PermissionRoute element={ClubRequirements} pageIds={routePageIds["Club-requirements"]} />
      } />
      <Route path="Club-requirements-add" element={
        <PermissionRoute element={ClubRequirementsAdd} pageIds={routePageIds["Club-requirements-add"]} />
      } />
      <Route path="Club-requirement-edit/:id" element={
        <PermissionRoute element={ClubRequirementsAdd} pageIds={routePageIds["Club-requirement-edit/:id"]} />
      } />
      <Route path="Club-view/:id" element={
        <PermissionRoute element={ClubView} pageIds={routePageIds["Club-view/:id"]} />
      } />
      <Route path="Club-add" element={
        <PermissionRoute element={ClubAdd} pageIds={routePageIds["Club-add"]} />
      } />
      <Route path="League-manager" element={
        <PermissionRoute element={LeagueManager} pageIds={routePageIds["League-manager"]} />
      } />
      <Route path="Club-staff-edit/:id" element={
        <PermissionRoute element={ClubStaffEdit} pageIds={routePageIds["Club-staff-edit/:id"]} />
      } />
      <Route path="Club-details-edit/:id" element={
        <PermissionRoute element={ClubDetailsEdit} pageIds={routePageIds["Club-details-edit/:id"]} />
      } />
      <Route path="Analysis" element={
        <PermissionRoute element={Analysis} pageIds={routePageIds["Analysis"]} />
      } />
      <Route path="Match-reports" element={
        <PermissionRoute element={MatchReport} pageIds={routePageIds["Match-reports"]} />
      } />
      <Route path="Match-reports-add" element={
        <PermissionRoute element={MatchReportsAdd} pageIds={routePageIds["Match-reports-add"]} />
      } />
      <Route path="Match-reports-edit/:id" element={
        <PermissionRoute element={MatchReportsAdd} pageIds={routePageIds["Match-reports-edit/:id"]} />
      } />
      <Route path="Match-reports-view/:id" element={
        <PermissionRoute element={MatchReportsView} pageIds={routePageIds["Match-reports-view/:id"]} />
      } />
      <Route path="Candidate-add/:id" element={
        <PermissionRoute element={CandidateAdd} pageIds={routePageIds["Candidate-add/:id"]} />
      } />
      <Route path="Transfer-sheets" element={
        <PermissionRoute element={TransferSheets} pageIds={routePageIds["Transfer-sheets"]} />
      } />
      <Route path="Reset-club" element={
        <PermissionRoute element={ResetClub} pageIds={routePageIds["Reset-club"]} />
      } />
    </Routes>
  );
}

function App() {
  window.isPreviewMode = window.location.pathname.includes('cmsctx');
  const testPath = window.isPreviewMode ? window.location.pathname.substring(0, window.location.pathname.lastIndexOf("/-/") + 3) : "/";
  
  return (
        <BrowserRouter>
          <Suspense fallback={
            <div style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(255,255,255,0.7)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ProgressSpinner style={{width: '50px', height: '50px'}} />
            </div>
          }>
            <Routes>
              <Route path="/auth/external-login" element={<ExternalLogin />} />
              <Route path={testPath + "login"} element={<Login />} />
              
              <Route path="/public/match-report/:id" element={
                <PermissionRoute element={MatchReportPublic} pageIds={routePageIds["Match-reports-view/:id"]} />
              } />
              <Route path={testPath + "*"} element={window.isPreviewMode ? null : <Master />}>
                <Route path="*" element={<AppRoutes />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
  );
}

export default App;